import { type VariantProps, cva } from "class-variance-authority";
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";

import { cn } from "../../lib/cn";
import { SlidingIndicator } from "./SlidingIndicator";

const CHIP_BASE =
  "press inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium transition-[color,background-color,border-color,box-shadow,scale] duration-[var(--dur-med)] ease-[var(--ease-out)] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-40";

/**
 * Still water (#2498): the member app's choice chips are glass pills. The
 * label is ink (never the muted grey) at weight 500; the chosen chip is the
 * teal fill with white text. The pane keeps the rim and a small shadow: the
 * pane's own 40 px drop is too heavy for a row of chips.
 */
const GLASS_IDLE =
  "glass rounded-full border-transparent text-fg [box-shadow:var(--glass-rim),var(--glass-shadow-sm)]";
const GLASS_CHOSEN =
  "rounded-full border-transparent bg-accent-strong text-fg-on-accent shadow-button";

const chipStyles = cva(CHIP_BASE, {
  variants: {
    tone: {
      accent: "",
      success: "",
      warning: "",
      neutral: "",
    },
    active: { true: "", false: "" },
  },
  compoundVariants: [
    {
      tone: "accent",
      active: true,
      class: "border-accent/40 bg-accent-soft text-accent-fg",
    },
    {
      tone: "success",
      active: true,
      class: "border-success/40 bg-success-soft text-success",
    },
    {
      tone: "warning",
      active: true,
      class: "border-warning/40 bg-warning-soft text-warning",
    },
    {
      tone: "neutral",
      active: true,
      class: "border-fg-muted/40 bg-bg-subtle text-fg",
    },
    {
      active: false,
      class:
        "border-border bg-transparent text-fg-muted [@media(hover:hover)]:hover:border-border-strong hover:bg-bg-subtle hover:text-fg",
    },
  ],
  defaultVariants: {
    tone: "accent",
    active: false,
  },
});

export interface ChipProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof chipStyles>, "active"> {
  active?: boolean;
  /** #2498: a glass pill, the chosen one teal. Opt in; the member app's choice chips use it. */
  glass?: boolean;
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(
  ({ className, tone, active = false, glass = false, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        glass ? cn(CHIP_BASE, active ? GLASS_CHOSEN : GLASS_IDLE) : chipStyles({ tone, active }),
        className,
      )}
      aria-pressed={active}
      {...props}
    />
  ),
);
Chip.displayName = "Chip";

export interface ChipGroupProps {
  label?: string;
  /** Accessible name for the group when no visible `label` shows. */
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
  title?: string;
  /** #2172 — single-select groups: the active fill glides between chips
   *  (`<SlidingIndicator/>`). Leave off for multi-select groups, where
   *  several active chips keep their own fills. */
  sliding?: boolean;
}

export function ChipGroup({
  label,
  ariaLabel,
  children,
  className,
  title,
  sliding = false,
}: ChipGroupProps) {
  return (
    <div
      className={cn("flex flex-wrap items-center gap-1.5", className)}
      title={title}
      {...(ariaLabel ? { role: "group", "aria-label": ariaLabel } : {})}
    >
      {label ? (
        <span className="text-[10px] font-medium uppercase tracking-wide text-fg-muted">
          {label}
        </span>
      ) : null}
      {sliding ? (
        <div className="relative isolate flex flex-wrap gap-1 rounded-md [&>[aria-pressed=true]]:bg-transparent">
          <SlidingIndicator />
          {children}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">{children}</div>
      )}
    </div>
  );
}
